const STORAGE_KEY = 'pivot-study-timer-state';
const UPDATE_INTERVAL_MS = 1000;
const USER_PATH_PREFIX = '/user';

const DEFAULT_STATE = {
    isRunning: false,
    accumulatedMs: 0,
    startedAt: null,
    isCollapsed: true,
};

const readState = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) {
            return null;
        }

        const { isRunning, accumulatedMs, startedAt, isCollapsed } = parsed;
        return {
            isRunning: Boolean(isRunning),
            accumulatedMs: Number.isFinite(accumulatedMs) ? Math.max(0, Number(accumulatedMs)) : 0,
            startedAt: Number.isFinite(startedAt) ? Number(startedAt) : null,
            isCollapsed: typeof isCollapsed === 'boolean' ? isCollapsed : true,
        };
    } catch (error) {
        console.warn('Failed to read timer state:', error);
        return null;
    }
};

const writeState = (state) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
        console.warn('Failed to persist timer state:', error);
    }
};

const clearState = () => {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.warn('Failed to clear timer state:', error);
    }
};

const extractElapsedTime = (elapsedMs) => {
    const totalSeconds = Math.floor(Math.max(0, elapsedMs) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return {
        minutes: minutes.toString(),
        seconds: seconds.toString().padStart(2, '0'),
    };
};

export default class StudyTimer {
    constructor() {
        if (!window.location.pathname.startsWith(USER_PATH_PREFIX)) {
            return;
        }

        this.elements = {};
        this.intervalId = null;
        this.state = { ...DEFAULT_STATE };

        this.initialize();
    }

    initialize() {
        this.createTimer();
        this.restoreState();
        this.applyCollapsedState();
        this.updateDisplay();

        if (this.state.isRunning) {
            this.startTicker();
        }
    }

    createTimer() {
        const container = document.createElement('section');
        container.className = 'study-timer';
        container.setAttribute('role', 'status');
        container.setAttribute('aria-live', 'polite');

        container.innerHTML = `
            <button type="button"
                    class="study-timer__toggle"
                    aria-label="タイマーの表示を切り替え"
                    aria-expanded="false"
                    data-role="study-timer-toggle">
                <i class="fa-solid fa-chevron-right study-timer__toggle-icon" data-role="study-timer-toggle-icon"></i>
            </button>
            <div class="study-timer__content">
                <div class="study-timer__label">学習タイマー</div>
                <div class="study-timer__display">
                    <span data-role="study-timer-minutes">0</span>
                    <span class="study-timer__unit">分</span>
                    <span data-role="study-timer-seconds">00</span>
                    <span class="study-timer__unit">秒</span>
                </div>
                <div class="study-timer__controls">
                    <button type="button"
                            class="study-timer__button"
                            data-action="start"
                            aria-label="スタート">
                        <i class="fa-solid fa-play"></i>
                    </button>
                    <button type="button"
                            class="study-timer__button"
                            data-action="stop"
                            aria-label="ストップ">
                        <i class="fa-solid fa-pause"></i>
                    </button>
                    <button type="button"
                            class="study-timer__button"
                            data-action="reset"
                            aria-label="クリア">
                        <i class="fa-solid fa-rotate-left"></i>
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(container);

        this.elements.container = container;
        this.elements.minutes = container.querySelector('[data-role="study-timer-minutes"]');
        this.elements.seconds = container.querySelector('[data-role="study-timer-seconds"]');
        this.elements.start = container.querySelector('[data-action="start"]');
        this.elements.stop = container.querySelector('[data-action="stop"]');
        this.elements.reset = container.querySelector('[data-action="reset"]');
        this.elements.toggle = container.querySelector('[data-role="study-timer-toggle"]');
        this.elements.toggleIcon = container.querySelector('[data-role="study-timer-toggle-icon"]');

        this.elements.start.addEventListener('click', () => this.start());
        this.elements.stop.addEventListener('click', () => this.stop());
        this.elements.reset.addEventListener('click', () => this.reset());
        this.elements.toggle.addEventListener('click', () => this.toggleCollapse());
        container.addEventListener('focusin', () => this.expand());
    }

    restoreState() {
        const saved = readState();
        if (!saved) {
            return;
        }

        this.state = { ...this.state, ...saved };

        if (this.state.isRunning && !this.state.startedAt) {
            // Recover from inconsistent state gracefully.
            this.state.isRunning = false;
            this.state.startedAt = null;
        }
    }

    persistState() {
        writeState(this.state);
    }

    computeElapsedMs() {
        if (!this.state.isRunning || !this.state.startedAt) {
            return this.state.accumulatedMs;
        }

        const now = Date.now();
        return this.state.accumulatedMs + Math.max(0, now - this.state.startedAt);
    }

    updateDisplay() {
        if (!this.elements.minutes || !this.elements.seconds) {
            return;
        }

        const elapsedMs = this.computeElapsedMs();
        const { minutes, seconds } = extractElapsedTime(elapsedMs);

        this.elements.minutes.textContent = minutes;
        this.elements.seconds.textContent = seconds;
    }

    start() {
        if (this.state.isRunning) {
            return;
        }

        this.state.isRunning = true;
        this.state.startedAt = Date.now();
        this.persistState();
        this.startTicker();
        this.updateDisplay();
    }

    stop() {
        if (!this.state.isRunning) {
            return;
        }

        this.state.accumulatedMs = this.computeElapsedMs();
        this.state.isRunning = false;
        this.state.startedAt = null;
        this.persistState();
        this.stopTicker();
        this.updateDisplay();
    }

    reset() {
        this.state = {
            ...DEFAULT_STATE,
            isCollapsed: this.state.isCollapsed,
        };
        clearState();
        this.persistState();
        this.stopTicker();
        this.updateDisplay();
    }

    startTicker() {
        if (this.intervalId) {
            return;
        }

        this.intervalId = window.setInterval(() => {
            this.updateDisplay();
        }, UPDATE_INTERVAL_MS);
    }

    stopTicker() {
        if (!this.intervalId) {
            return;
        }

        window.clearInterval(this.intervalId);
        this.intervalId = null;
    }

    toggleCollapse() {
        this.state.isCollapsed = !this.state.isCollapsed;
        this.applyCollapsedState();
        this.persistState();
    }

    expand() {
        if (!this.state.isCollapsed) {
            return;
        }

        this.state.isCollapsed = false;
        this.applyCollapsedState();
        this.persistState();
    }

    applyCollapsedState() {
        if (!this.elements.container) {
            return;
        }

        this.elements.container.classList.toggle('is-collapsed', this.state.isCollapsed);

        if (this.elements.toggle) {
            this.elements.toggle.setAttribute('aria-expanded', this.state.isCollapsed ? 'false' : 'true');
        }

        if (this.elements.toggleIcon) {
            this.elements.toggleIcon.classList.toggle('is-expanded', !this.state.isCollapsed);
        }
    }
}
